import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { POLLING_INTERVAL_MS } from "../../constants/polling-constants";
import { queryKeys } from "../../lib/queryKeys";
import {
  getOpcdQueriesList,
  getOpcdQueryDetail,
  submitOpcdQueryResponse,
} from "../../services";
import type {
  OpcdQueriesListResponse,
  OpcdQuery,
  OpcdQuerySubmitPayload,
} from "../../types/opcd-queries";
import {
  extractOpcdQueriesListResponse,
  extractOpcdQueryDetail,
  isOpcdQueryPending,
} from "../../utils/opcd-query-utils";

const fetchOpcdQueries = async (
  providerNpi: string
): Promise<OpcdQueriesListResponse> => {
  const response = await getOpcdQueriesList(providerNpi);
  return extractOpcdQueriesListResponse(response.data);
};

export const useOpcdQueryList = (
  providerNpi: string,
  options?: { polling?: boolean; enabled?: boolean }
) =>
  useQuery({
    queryKey: queryKeys.opcdQueriesList(providerNpi),
    queryFn: () => fetchOpcdQueries(providerNpi),
    enabled: (options?.enabled ?? Boolean(providerNpi)) && Boolean(providerNpi),
    refetchInterval: options?.polling ? POLLING_INTERVAL_MS : false,
  });

export const useOpcdQueriesPendingCount = (
  providerNpi?: string,
  options?: { polling?: boolean; enabled?: boolean }
) =>
  useQuery({
    queryKey: queryKeys.opcdQueriesPendingCount(providerNpi ?? ""),
    queryFn: async () => {
      const response = await fetchOpcdQueries(providerNpi!);
      return response.queries.filter((query) =>
        isOpcdQueryPending(query.status)
      ).length;
    },
    enabled:
      (options?.enabled ?? Boolean(providerNpi)) && Boolean(providerNpi),
    refetchInterval: options?.polling ? POLLING_INTERVAL_MS : false,
  });

export const useOpcdQueryDetail = (
  queryId?: string,
  providerNpi?: string
) =>
  useQuery<OpcdQuery | null>({
    queryKey: queryKeys.opcdQueryDetail(queryId ?? "", providerNpi ?? ""),
    queryFn: async () => {
      const response = await getOpcdQueryDetail(queryId!, providerNpi!);
      return extractOpcdQueryDetail(response.data);
    },
    enabled: Boolean(queryId && providerNpi),
  });

export const useSubmitOpcdQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      queryId,
      body,
    }: {
      queryId: string;
      body: OpcdQuerySubmitPayload;
    }) => submitOpcdQueryResponse(queryId, body),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["opcdQueriesList"] });
      void queryClient.invalidateQueries({
        queryKey: ["opcdQueriesPendingCount"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["opcdQueryDetail", variables.queryId],
      });
    },
  });
};
